"use client"

import axios from "axios";
import { useEffect, useRef, useState } from "react";
import { NotificationManager } from "react-notifications";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import OptionalOption from "../../../components/FilterComponents/OptionalOption";
import useAuth from "../../../hooks/useAuth";
import { BASE_URL } from "../../../utils/Element";
import { ContractToCloseStatusOptions, LeasetypeOptions } from "../../../utils/options";

/* ==================== CONFIGURATIONS ==================== */

const getFieldsByTab = (activeTab) => {
  if (activeTab === 0) {
    return {
      "Current Phase": ["Is", "Is Not", "Contains", "Is Empty", "Is Not Empty"],
      "Next Action": ["Is", "Is Not", "Contains", "Is Empty", "Is Not Empty"],
      "Property": ["Is", "Is Not", "Contains", "Is Any", "Is All", "Is Empty", "Is Not Empty"],
      "Key Contact": ["Is", "Is Not", "Is Any", "Is Empty", "Is Not Empty"],
    };
  }

  if (activeTab === 2) { // Transaction Dashboard
    return {
      "Property": ["Is", "Is Not", "Contains", "Is Any", "Is All"],
      "Advisor": ["Is", "Is Not", "Is Any", "Is All"],
      "Status": ["Is", "Is Not", "Is Any", "Is All"],
      "Acquisition Contact": ["Is", "Is Not", "Is Any", "Is Empty", "Is Not Empty"],
      "Disposition Contact": ["Is", "Is Not", "Is Any", "Is Empty", "Is Not Empty"],
      "Executed LOI Date": ["On", "Before", "After", "Between", "In Last X Days", "Is Empty", "Is Not Empty"],
      "Under Contract Date": ["On", "Before", "After", "Between", "In Last X Days", "Is Empty", "Is Not Empty"],
      "Expected Close Date": ["On", "Before", "After", "Between", "In Last X Days","In Next X Days", "Is Empty", "Is Not Empty"],
      "Closed Date": ["On", "Before", "After", "Between", "In Last X Days", "Is Empty", "Is Not Empty"],
    };
  }

  if (activeTab === 3) { // Market Metrics
    return {
      "Property": ["Is", "Is Not", "Contains", "Is Any", "Is All"],
      "Advisor": ["Is", "Is Not", "Is Any", "Is All"],
      "Status": ["Is", "Is Not", "Is Any", "Is All"],           // ✅ Added
      "Lease Type": ["Is", "Is Not", "Is Any", "Is All"],
      "Lease Term Remaining": ["Is", "Greater Than", "Less Than", "Between", "Is Empty", "Is Not Empty"],
      "NOI": ["Is", "Greater Than", "Less Than", "Between", "Is Empty", "Is Not Empty"],
      "Contract Price": ["Is", "Greater Than", "Less Than", "Between", "Is Empty", "Is Not Empty"],
      "Contract Cap Rate": ["Is", "Greater Than", "Less Than", "Between", "Is Empty", "Is Not Empty"],
      "% of Asking Price": ["Is", "Greater Than", "Less Than", "Between", "Is Empty", "Is Not Empty"],
    };
  }

  return {};
};

const DROPDOWN_FIELDS = {
  "Property": { api: "properties_unique", isAsync: true },
  "Key Contact": { api: "contacts", isAsync: true },
  "Acquisition Contact": { api: "contacts", isAsync: true },
  "Disposition Contact": { api: "contacts", isAsync: true },
  "Advisor": { api: "users", roleFilter: 3 },
  "Status": { options: ContractToCloseStatusOptions },
  "Lease Type": { options: LeasetypeOptions },
  "Current Phase": { api: "contract_to_close", fieldType: "current_phase" },
  "Next Action": { api: "contract_to_close", fieldType: "next_action" },
};

const DATE_FIELDS = [
  "Executed LOI Date",
  "Under Contract Date",
  "Closed Date",
  "Expected Close Date"
];

const NUMERIC_FIELDS = [
  "Lease Term Remaining",
  "NOI",
  "Contract Price",
  "Contract Cap Rate",
  "% of Asking Price"
];

const PREFIX_MAP = {
  "Executed LOI Date": "executed_loi_",
  "Under Contract Date": "under_contract_",
  "Closed Date": "closed_",
  "Expected Close Date": "expected_close_",
  "Lease Term Remaining": "lease_term_",
  "NOI": "noi_",
  "Contract Price": "contract_price_",
  "Contract Cap Rate": "contract_cap_rate_",
  "% of Asking Price": "asking_price_",
};

const JOIN_OPTIONS = [
  { label: "AND", value: "AND" },
  { label: "OR", value: "OR" },
];

const selectPortalStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 99999 }),
  menu: (base) => ({ ...base, zIndex: 99999 }),
  control: (base) => ({ ...base, minHeight: "38px" }),
};

function ContractCloseAdvanceFilterData({
  filterData,
  onSetFilterData,
  isSidebarOpen,
  activeTab,
  from
}) {

  const [config] = useAuth();
  const [groups, setGroups] = useState([]);
  const [dropdownOptions, setDropdownOptions] = useState({});
  const fetchedFieldsRef = useRef(new Set());

  const availableFields = getFieldsByTab(activeTab);

  // Initialize groups
  useEffect(() => {
    if (isSidebarOpen) {
      setGroups(
        filterData?.length > 0
          ? filterData
          : [{ id: Date.now(), condition: "AND", filters: [{ join: null, field: "", operator: "", value: "" }] }]
      );
    }
  }, [isSidebarOpen, filterData]);

  // Fetch options
  useEffect(() => {
    const fetchMissingOptions = async () => {
      const fieldsToFetch = new Set();
      groups.forEach((group) => {
        group.filters.forEach((filter) => {
          if (DROPDOWN_FIELDS[filter.field] && !fetchedFieldsRef.current.has(filter.field)) {
            fieldsToFetch.add(filter.field);
          }
        });
      });

      for (const field of fieldsToFetch) {
        fetchedFieldsRef.current.add(field);
        try {
          const options = await fetchDropdownOptions(field);
          setDropdownOptions((prev) => ({ ...prev, [field]: options }));
        } catch (err) {
          console.error(`Failed to load options for ${field}`, err);
        }
      }
    };

    if (isSidebarOpen && groups.length > 0) {
      fetchMissingOptions();
    }
  }, [groups, isSidebarOpen]);

  const fetchDropdownOptions = async (field, search = "") => {
    const configData = DROPDOWN_FIELDS[field];
    if (!configData) return [];

    try {
      let url;
      if (configData.api === "contract_to_close") {
        url = `${BASE_URL}/get-datalisting?list=contract_to_close&search=${search}&field=${configData.fieldType}`;
      } else if (configData.roleFilter) {
        url = `${BASE_URL}/get-datalisting?list=usersList&role_id=${configData.roleFilter}&search=${search}`;
      } else {
        url = `${BASE_URL}/get-datalisting?list=${configData.api}&search=${search}`;
      }

      const res = await axios.get(url, config);
      const data = res?.data?.data || {};

      if (configData.api === "properties_unique") {
        return data.properties?.map((el) => ({
          value: el.id,
          label: el.property_name,
        })) || [];
      }

      if (configData.api === "contacts") {
        return data.contact_list?.map((el) => ({
          value: el.id,
          label: `${el.first_name} ${el.last_name}`,
        })) || [];
      }

      if (configData.api === "users" || configData.roleFilter) {
        return data.users?.map((el) => ({
          value: el.id,
          label: `${el.first_name} ${el.last_name}`,
        })) || [];
      }

      if (configData.api === "contract_to_close") {
        const contractData = res?.data?.contract_to_close || {};
        return Object.entries(contractData).map(([_, label]) => ({
          value: label,
          label: label,
        }));
      }

      return configData.options || [];
    } catch (err) {
      if (err.response?.data?.message !== "Too Many Attempts.") {
        NotificationManager.error(err.response?.data?.message || "Failed to load options");
      }
      return [];
    }
  };

  const updateState = (updated) => {
    setGroups(updated);
    onSetFilterData(updated);
  };

  const addFilterRow = (groupIndex) => {
    const updated = [...groups];
    updated[groupIndex].filters.push({ join: "AND", field: "", operator: "", value: "" });
    updateState(updated);
  };

  const updateRow = (groupIndex, filterIndex, key, value) => {
    const updated = [...groups];
    const filter = updated[groupIndex].filters[filterIndex];

    if (key === "field") {
      filter.field = value?.value ?? value;
      filter.operator = "";
      filter.value = "";
    } else if (key === "operator") {
      const newOp = value?.value ?? value;
      filter.operator = newOp;

      const isMulti = ["Is Any", "Is All", "Exclude Any"].includes(newOp);
      
      if (DATE_FIELDS.includes(filter.field) || NUMERIC_FIELDS.includes(filter.field)) {
        const prefix = PREFIX_MAP[filter.field] || "";
        filter.value = { [`${prefix}count`]: "", [`${prefix}start_count`]: "", [`${prefix}end_count`]: "" };
      } else {
        filter.value = isMulti ? [] : "";
      }
    } else {
      filter[key] = value;
    }

    updateState(updated);
  };

  const handleDateChange = (groupIndex, filterIndex, subKey, subValue) => {
    const updated = [...groups];
    const filter = updated[groupIndex].filters[filterIndex];
    filter.value = { ...filter.value || {}, [subKey]: subValue };
    updateState(updated);
  };

  const removeRow = (groupIndex, filterIndex) => {
    const updated = [...groups];
    if (updated[groupIndex].filters.length === 1) {
      if (updated.length > 1) updated.splice(groupIndex, 1);
      else updated[groupIndex].filters[0] = { join: null, field: "", operator: "", value: "" };
    } else {
      updated[groupIndex].filters.splice(filterIndex, 1);
    }
    updateState(updated);
  };

  /* ==================== RENDER VALUE INPUT ==================== */
  const renderValueInput = (groupIndex, filterIndex, filter) => {
    if (!filter.operator || ["Is Empty", "Is Not Empty"].includes(filter.operator)) return null;

    const isDateOrNumeric = DATE_FIELDS.includes(filter.field) || NUMERIC_FIELDS.includes(filter.field);
    if (isDateOrNumeric) return null;

    // Special Case: Property + Contains = Text Input
    if (filter.field === "Property" && filter.operator === "Contains") {
      return (
        <input
          className="flex-1 border px-2 py-1 rounded dark-H body-N"
          placeholder="Enter Property Name"
          value={filter.value || ""}
          onChange={(e) => updateRow(groupIndex, filterIndex, "value", e.target.value)}
        />
      );
    }

    const configData = DROPDOWN_FIELDS[filter.field];
    if (!configData) {
      return (
        <input
          className="flex-1 border px-2 py-1 rounded dark-H body-N"
          placeholder="Enter Value"
          value={filter.value || ""}
          onChange={(e) => updateRow(groupIndex, filterIndex, "value", e.target.value)}
        />
      );
    }

    const isMulti = ["Is Any", "Is All", "Exclude Any"].includes(filter.operator);
    const isAsync = configData.isAsync === true;

    if (isAsync) {
      return (
        <AsyncSelect
          className="flex-1 dark-H body-N"
          placeholder="Search..."
          menuPortalTarget={document.body}
          menuPosition="fixed"
          styles={selectPortalStyles}
          isSearchable
          isMulti={isMulti}
          loadOptions={(input) => fetchDropdownOptions(filter.field, input)}
          value={filter.value}
          onChange={(selected) => updateRow(groupIndex, filterIndex, "value", selected)}
          cacheOptions
          defaultOptions
          menuShouldBlockScroll={true}
          menuShouldScrollIntoView={true}
        />
      );
    }

    // Static Dropdown (Status, Lease Type, etc.)
    const fieldOptions = dropdownOptions[filter.field] || configData.options || [];
    return (
      <Select
        className="flex-1 dark-H body-N"
        placeholder="Select Value"
        menuPortalTarget={document.body}
        menuPosition="fixed"
        styles={selectPortalStyles}
        isSearchable
        isMulti={isMulti}
        options={fieldOptions}
        value={
          isMulti
            ? fieldOptions.filter((opt) => Array.isArray(filter.value) && filter.value.includes(opt.value))
            : fieldOptions.find((opt) => opt.value === filter.value) || null
        }
        onChange={(selected) => {
          const newValue = isMulti ? selected?.map(opt => opt.value) : selected?.value || "";
          updateRow(groupIndex, filterIndex, "value", newValue);
        }}
        menuShouldBlockScroll={true}
        menuShouldScrollIntoView={true}
      />
    );
  };

  const shouldShowSimpleDaysInput = (filter) => {
    return DATE_FIELDS.includes(filter.field) &&
      ["In Last X Days", "In Next X Days"].includes(filter.operator);
  };

  // Archive Mode
  if (from === "contractclosearchive") {
    return (
      <div>
        <p className="head-4 dark-H">Date Range</p>
        <OptionalOption
          activeTab="date"
          onSetLastDate={(value) => onSetFilterData((prev) => ({ ...prev, count: value }))}
          date={filterData?.count}
          onSetDateRange1={(value) => onSetFilterData((prev) => ({ ...prev, start_count: value }))}
          date2={filterData?.start_count}
          onSetDateRange2={(value) => onSetFilterData((prev) => ({ ...prev, end_count: value }))}
          date3={filterData?.end_count}
          onClear={() => onSetFilterData((prev) => ({ ...prev, count: "", start_count: "", end_count: "" }))}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6 text-black">
      {groups.map((group, groupIndex) => (
        <div key={group.id} className="bg-white space-y-3 p-4 rounded-lg">
          {groupIndex > 0 && (
            <div className="text-sm font-medium text-gray-600">{group.condition} Group</div>
          )}

          {group.filters.map((filter, index) => {
            const prefix = PREFIX_MAP[filter.field] || "";

            return (
              <div key={index} className="space-y-3">
                <div className="flex items-center gap-2">
                  {index > 0 && (
                    <Select
                      className="w-24 dark-H body-N"
                      options={JOIN_OPTIONS}
                      value={filter.join ? { label: filter.join, value: filter.join } : null}
                      onChange={(e) => updateRow(groupIndex, index, "join", e.value)}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      styles={selectPortalStyles}
                    />
                  )}

                  <Select
                    className="flex-1 dark-H body-N"
                    placeholder="Select Field"
                    isSearchable
                    options={Object.keys(availableFields).map((f) => ({ label: f, value: f }))}
                    value={filter.field ? { label: filter.field, value: filter.field } : null}
                    onChange={(e) => updateRow(groupIndex, index, "field", e)}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={selectPortalStyles}
                  />

                  <Select
                    className="flex-1 dark-H body-N"
                    placeholder="Condition"
                    isDisabled={!filter.field}
                    options={filter.field && availableFields[filter.field]
                      ? availableFields[filter.field].map((c) => ({ label: c, value: c }))
                      : []
                    }
                    value={filter.operator ? { label: filter.operator, value: filter.operator } : null}
                    onChange={(e) => updateRow(groupIndex, index, "operator", e)}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={selectPortalStyles}
                  />

                  {renderValueInput(groupIndex, index, filter)}

                  <button
                    className="text-red-500 hover:text-red-700 px-3 py-2"
                    onClick={() => removeRow(groupIndex, index)}
                  >
                    ✕
                  </button>
                </div>

                {shouldShowSimpleDaysInput(filter) && (
                  <div className="pl-2">
                    <input
                      type="number"
                      className="dark-H body-N w-full border px-3 py-2 rounded"
                      placeholder="Enter number of days"
                      min={0}
                      value={filter.value?.[`${prefix}count`] || ""}
                      onChange={(e) => handleDateChange(groupIndex, index, `${prefix}count`, e.target.value)}
                    />
                  </div>
                )}

                {filter.operator &&
                  !["Is Empty", "Is Not Empty"].includes(filter.operator) &&
                  !shouldShowSimpleDaysInput(filter) &&
                  (DATE_FIELDS.includes(filter.field) || NUMERIC_FIELDS.includes(filter.field)) && (
                    <div className="pl-2">
                      <OptionalOption
                        activeTab="date"
                        type={NUMERIC_FIELDS.includes(filter.field) ? "number" : undefined}
                        onSetLastDate={(v) => handleDateChange(groupIndex, index, `${prefix}count`, v)}
                        date={filter.value?.[`${prefix}count`] || ""}
                        onSetDateRange1={(v) => handleDateChange(groupIndex, index, `${prefix}start_count`, v)}
                        date2={filter.value?.[`${prefix}start_count`] || ""}
                        onSetDateRange2={(v) => handleDateChange(groupIndex, index, `${prefix}end_count`, v)}
                        date3={filter.value?.[`${prefix}end_count`] || ""}
                        onClear={() => {
                          handleDateChange(groupIndex, index, `${prefix}count`, "");
                          handleDateChange(groupIndex, index, `${prefix}start_count`, "");
                          handleDateChange(groupIndex, index, `${prefix}end_count`, "");
                        }}
                      />
                    </div>
                  )}
              </div>
            );
          })}

          <button className="green-H head-5 mt-3" onClick={() => addFilterRow(groupIndex)}>
            + Add Filter
          </button>
        </div>
      ))}
    </div>
  );
}

export default ContractCloseAdvanceFilterData;